'use client';

import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

export interface MetricGridProps {
  /** MetricCell children (or any other tile content). */
  children: ReactNode;
  /**
   * Number of columns. Pass a number for a fixed column count, or a responsive object.
   * Default 4.
   */
  columns?: number | { xs?: number; sm?: number; md?: number; lg?: number };
  /** Row gap between cells. Default 2.5 (theme spacing). */
  rowGap?: number;
  /** Column gap between cells. Default 3 (theme spacing). */
  columnGap?: number;
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

function gridTemplate(columns: NonNullable<MetricGridProps['columns']>) {
  if (typeof columns === 'number') {
    return `repeat(${columns}, minmax(0, 1fr))`;
  }
  return {
    xs: `repeat(${columns.xs ?? 1}, minmax(0, 1fr))`,
    sm: columns.sm ? `repeat(${columns.sm}, minmax(0, 1fr))` : undefined,
    md: columns.md ? `repeat(${columns.md}, minmax(0, 1fr))` : undefined,
    lg: columns.lg ? `repeat(${columns.lg}, minmax(0, 1fr))` : undefined,
  };
}

export function MetricGrid({ children, columns = 4, rowGap = 2.5, columnGap = 3, sx }: MetricGridProps) {
  return (
    <Box sx={composeSx({ display: 'grid', gridTemplateColumns: gridTemplate(columns), rowGap, columnGap }, sx)}>
      {children}
    </Box>
  );
}

MetricGrid.displayName = 'MetricGrid';

export default MetricGrid;
